import { action, makeAutoObservable, observable, runInAction } from 'mobx';
import { TrpcClient } from 'src/AppState/TrpcClient';
import z from 'zod';

const POLL_INTERVAL_MS = 3000;

export type BetBuilderPriceStatusType = 'loading' | 'ok' | 'expired' | 'suspended' | 'error';

export class BetBuilderPriceState {
    @observable public currentPrice: number | null = null;
    @observable public currentPriceFractional: string | null = null;
    @observable public status: BetBuilderPriceStatusType = 'loading';

    private intervalId: ReturnType<typeof setInterval> | null = null;

    public constructor(
        private readonly trpcClient: TrpcClient,
        private readonly geniusMarketId: string,
        private readonly stake: number
    ) {
        makeAutoObservable(this);
        this.startPolling();
    }

    @action private async fetchPrice(): Promise<void> {
        try {
            const result = await this.trpcClient.getBetBuilderPrice(this.geniusMarketId, this.stake);

            runInAction(() => {
                if (result.type === 'ok') {
                    this.status = 'ok';
                    this.parsePriceData(result.data);
                } else if (result.type === 'expired') {
                    this.status = 'expired';
                    this.stopPolling();
                } else if (result.type === 'suspended') {
                    this.status = 'suspended';
                } else {
                    this.status = 'error';
                }
            });
        } catch {
            runInAction(() => {
                this.status = 'error';
            });
        }
    }

    private parsePriceData(data: unknown): void {
        const PriceDataSchema = z.object({
            stake: z.number(),
            price: z.number(),
            payout: z.number(),
            priceFractional: z.string(),
            priceBeforeRounding: z.number(),
            payoutBeforeRounding: z.number(),
        });

        const result = PriceDataSchema.safeParse(data);

        if (!result.success) {
            return;
        }

        this.currentPrice = result.data.price;
        this.currentPriceFractional = result.data.priceFractional;
    }

    private startPolling(): void {
        void this.fetchPrice();
        this.intervalId = setInterval(() => {
            void this.fetchPrice();
        }, POLL_INTERVAL_MS);
    }

    private stopPolling(): void {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
    }

    public dispose(): void {
        this.stopPolling();
    }

    public get livePriceInfo(): string {
        const { status, currentPriceFractional } = this;

        switch (status) {
            case 'ok':
                return currentPriceFractional !== null && currentPriceFractional !== ''
                    ? `Live: ${currentPriceFractional}`
                    : '';
            case 'expired':
                return 'Market expired';
            case 'suspended':
                return 'Market suspended';
            default:
                return 'Loading...';
        }
    }
}
